import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { EmptyTable } from "components/Datatable/components/EmptyTable";
import { EmailData, EmailGroup } from "../../types";
import { getEmailTypeLabel, EmailType } from "@/pages/conciliation/email-report/components/conciliation-email-report-types";
import { useMemo } from "react";

interface GroupedEmailTableProps {
  data: EmailData[];
  columns: any[];
}

export function GroupedEmailTable({ data, columns }: GroupedEmailTableProps) {
  const groupedData = useMemo(() => {
    const groups: EmailGroup[] = [];

    const typeOrder = [
      EmailType.CONFERENCIA_CAIXA,
      EmailType.COMPROVANTE_DEPOSITOS,
      EmailType.LANCAMENTO_DEPOSITOS,
    ];

    typeOrder.forEach(type => {
      const emailsForType = data?.filter((item: EmailData) => item.type === type);
      if (emailsForType.length > 0) {
        groups.push({
          type,
          label: getEmailTypeLabel(type),
          emails: emailsForType,
        });
      }
    });

    return groups;
  }, [data]);

  return (
    <Table className="overflow-x-auto">
      <TableHeader className="sticky top:0 bg-[#F9FBFC] z-10 shadow-sm">
        <TableRow>
          {columns.map((column: any, i: number) => (
            <TableHead
              key={column.id || i}
              className={`${column.sticky ? `sticky ${column.sticky} border-r bg-[#F9FBFC] z-20` : ""} min-w-[180px] px-4`}
            >
              {typeof column.header === 'function' ? column.header() : column.header}
            </TableHead>
          ))}
        </TableRow>
      </TableHeader>
      <TableBody className="relative overflow-x-auto">
        {data?.length > 0 ? (
          groupedData.map((group) => (
            <>
              <TableRow key={`group-${group.type}`} className="bg-gray-50/50">
                <TableCell colSpan={columns.length} className="py-3 sticky left-0 z-10 px-4 bg-gray-50/50">
                  <div className="font-medium flex items-center gap-3">
                    <span className="text-lg">📧</span>
                    <span className="text-gray-700">{group.label}</span>
                    <span className="bg-gray-200 text-gray-600 px-2 py-1 rounded-full text-xs">
                      {group.emails.length} e-mail(s)
                    </span>
                  </div>
                </TableCell>
              </TableRow>

              {group.emails.map((email, index) => (
                <TableRow
                  key={`${group.type}-${index}`}
                  className={`transition-all duration-300 ${
                    email.status === 'REJECTED' ? 'bg-gray-50 opacity-50' : ''
                  }`}
                >
                  {columns.map((column: any, colIndex: number) => {
                    const mockRow = {
                      original: email,
                      getValue: (key: string) => email[key as keyof typeof email],
                    };

                    return (
                      <TableCell
                        key={`${group.type}-${index}-${colIndex}`}
                        className={`${column.className ?? ""} ${column.sticky ? `sticky ${column.sticky} z-10 ${column.sticky === 'right-0' ? 'border-l bg-white' : 'border-r bg-white'}` : ""} min-w-[180px] px-4`}
                      >
                        {column.cell
                          ? column.cell({ row: mockRow })
                          : email[column.accessorKey as keyof typeof email]}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </>
          ))
        ) : (
          <TableRow>
            <TableCell colSpan={columns.length}>
              <EmptyTable text="Nenhum e-mail encontrado para esta conciliação." />
            </TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  );
}
